// 📄 pages/SAP-BTP/components/DetalleIflowModal.tsx
// Detalle de un iFlow del repositorio (F3): resumen + datos técnicos,
// diagrama (visor, plan-visor-diagrama-iflows.md), documentación, historial
// de versiones y las acciones (descargar, nueva
// versión, editar, publicar en un equipo, borrar). Las acciones se hacen
// dentro del mismo modal (`modo`) para no apilar modales.
//
// Quién puede qué lo decide el backend; acá solo se ocultan los botones que
// no aplican (`puedeGestionar`, org archivada).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  X, Download, Upload, Pencil, Trash2, Send, RefreshCw, FileText, ArrowRight, ShieldAlert, History, Users, BadgeCheck, SlidersHorizontal, EyeOff,
  PencilRuler
} from 'lucide-react';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';
import { useAuth } from '../../../context/AuthContext.tsx';
import { useUserProjects } from '../../../context/ProjectsContext.tsx';
import {
  AnalisisIflow, DatosIflow, ESTADOS_IFLOW, Iflow, IflowApiError, VersionIflow, MAX_DOCUMENTOS,
  agregarDocumentosIflow, borrarDocumentoIflow, borrarIflow, descargarDocumentoIflow, descargarIflow, descargarSinValores,
  editarIflow, listarVersiones, publicarIflow, subirNuevaVersion, tamanoLegible
} from '../../../services/iflowService.ts';
import { DetalleTecnico } from './DetalleTecnico.tsx';
import AvisosAnalisis from './AvisosAnalisis.tsx';
import FormDatosIflow, { inputCls, labelCls } from './FormDatosIflow.tsx';
import SelectorDocumentos from './SelectorDocumentos.tsx';
import DiagramaIflow from './DiagramaIflow.tsx';
import AdaptarValores from './AdaptarValores.tsx';
import { disenoDesdeIflow } from '../../../services/disenoService.ts';
import { ElegirZip, prepararZip } from './SubirIflowModal.tsx';

interface DetalleIflowModalProps {
  iflow: Iflow;
  esPersonal: boolean;
  puedeGestionar: boolean;
  soloLectura: boolean;
  onCerrar: () => void;
  // Recargar la lista (el detalle se actualiza con el iFlow nuevo que baja el padre).
  onCambio: () => Promise<void> | void;
  onBorrado: () => void;
  onAviso: (mensaje: string) => void;
  // "Crear boceto" (E5): abre en el diseñador el boceto recién creado a partir de este iFlow.
  onCrearDiseno?: (disenoId: string) => void;
}

type Pestana = 'resumen' | 'diagrama' | 'documentos' | 'versiones';
type Modo = 'ver' | 'editar' | 'version' | 'publicar' | 'adaptar';

const fechaHora = (ts: any) =>
  ts?.toDate ? ts.toDate().toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

const datosEditables = (i: Iflow): DatosIflow => ({
  titulo: i.titulo,
  descripcion: i.descripcion,
  sistemaOrigen: i.sistemaOrigen,
  sistemaDestino: i.sistemaDestino,
  solucion: i.solucion,
  etiquetas: i.etiquetas,
  estado: i.estado,
  proyectoId: i.proyectoId,
  notas: i.notas
});

const Campo = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <dt className="text-[11px] uppercase tracking-wider text-gray-500">{label}</dt>
    <dd className="text-sm text-white mt-0.5 break-words">{children}</dd>
  </div>
);

export default function DetalleIflowModal({
  iflow, esPersonal, puedeGestionar, soloLectura, onCerrar, onCambio, onBorrado, onAviso, onCrearDiseno
}: DetalleIflowModalProps) {
  const { user, organizations } = useAuth();
  const { proyectos } = useUserProjects();
  const gestionar = puedeGestionar && !soloLectura;
  // "Dueño" de los valores (espejo de veValoresReales del backend): org personal, autor u owner de la org.
  const esDueno = esPersonal || iflow.autorUid === user?.uid || organizations.find((o) => o.id === iflow.orgId)?.role === 'owner';

  const [pestana, setPestana] = useState<Pestana>('resumen');
  const [modo, setModo] = useState<Modo>('ver');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Solo el contenido hace scroll (encabezado con la X y pie siempre visibles);
  // si aparece un error, se vuelve arriba para que se vea.
  const cuerpoRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (error) cuerpoRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); }, [error]);
  // "Editar" desde el panel del diagrama: abre "Adaptar valores" enfocado en ese elemento.
  const [focoAdaptar, setFocoAdaptar] = useState('');
  const [confirmar, setConfirmar] = useState<null | { tipo: 'iflow' } | { tipo: 'documento'; id: string; nombre: string }>(null);

  // Documentos
  const [docsNuevos, setDocsNuevos] = useState<File[]>([]);
  // Versiones
  const [versiones, setVersiones] = useState<VersionIflow[] | null>(null);
  // Editar
  const [datos, setDatos] = useState<DatosIflow>(datosEditables(iflow));
  // Nueva versión
  const [zipNuevo, setZipNuevo] = useState<File | null>(null);
  const [stagingPath, setStagingPath] = useState('');
  const [analisis, setAnalisis] = useState<AnalisisIflow | null>(null);
  const [confirmaFijos, setConfirmaFijos] = useState(false);
  const [vaciarParametros, setVaciarParametros] = useState(true);
  const [nota, setNota] = useState('');
  // Publicar
  const destinos = useMemo(
    () => organizations.filter((o) => o.type !== 'personal' && o.role !== 'invitado' && !o.archivada),
    [organizations]
  );
  const [destino, setDestino] = useState('');
  const nombreOrg = (id: string) => organizations.find((o) => o.id === id)?.name || 'otra organización';
  const publicacionPrevia = (iflow.publicaciones || []).find((p) => p.orgId === destino);

  const proyecto = proyectos.find((p) => p.id === iflow.proyectoId);
  const estado = ESTADOS_IFLOW.find((e) => e.value === iflow.estado)?.label || iflow.estado;

  // Las versiones se (re)cargan al abrir la pestaña o cuando cambia la revisión.
  useEffect(() => {
    if (pestana !== 'versiones') return;
    setVersiones(null);
    listarVersiones(iflow.orgId, iflow.id).then(setVersiones).catch(() => setError('No se pudo cargar el historial de versiones.'));
  }, [pestana, iflow.orgId, iflow.id, iflow.versionActual]);

  const volver = () => {
    setModo('ver');
    setFocoAdaptar('');
    setError(null);
    setZipNuevo(null);
    setAnalisis(null);
    setNota('');
  };

  /** Ejecuta una acción con spinner y manejo de error. */
  const accion = async (fn: () => Promise<void>) => {
    setError(null);
    setOcupado(true);
    try {
      await fn();
    } catch (err: any) {
      setError(err instanceof IflowApiError || err?.message ? err.message : 'No se pudo completar la acción.');
    } finally {
      setOcupado(false);
    }
  };

  const guardarEdicion = () => accion(async () => {
    if (!datos.titulo.trim() || !datos.descripcion.trim()) throw new Error('El título y la descripción son obligatorios.');
    await editarIflow(iflow.orgId, iflow.id, datos);
    await onCambio();
    onAviso('Cambios guardados.');
    volver();
  });

  const elegirVersion = (file: File | undefined) => {
    if (!file) return;
    setZipNuevo(file);
    accion(async () => {
      try {
        const r = await prepararZip(iflow.orgId, file);
        if (r.analisis.idTecnico !== iflow.idTecnico) {
          throw new Error(`El archivo es otro iFlow (${r.analisis.idTecnico}); súbelo como un iFlow nuevo.`);
        }
        setStagingPath(r.stagingPath);
        setAnalisis(r.analisis);
        setConfirmaFijos(false);
      } catch (err) {
        setZipNuevo(null);
        throw err;
      }
    });
  };

  const guardarVersion = () => accion(async () => {
    const r = await subirNuevaVersion(iflow.orgId, iflow.id, { stagingPath, note: nota, stripParameterValues: vaciarParametros, confirmFixedValues: confirmaFijos });
    await onCambio();
    onAviso(`Versión ${r.version} subida.`);
    volver();
  });

  const publicar = () => accion(async () => {
    const org = destinos.find((o) => o.id === destino);
    if (!org) throw new Error('Elige una organización.');
    const r = await publicarIflow(iflow.orgId, iflow.id, org.id);
    await onCambio();
    const docs = r.documentosAgregados ? `${r.documentosAgregados} documento${r.documentosAgregados === 1 ? '' : 's'}` : '';
    onAviso(!r.actualizado
      ? `Publicado en ${org.name}. Tu copia personal se mantiene.`
      : r.soloDocumentos
        ? `El .zip de ${org.name} ya estaba al día; se agregaron ${docs}.`
        : `Copia de ${org.name} actualizada a la revisión ${r.version}${docs ? ` (+${docs})` : ''}.`);
    volver();
  });

  const subirDocumentos = () => accion(async () => {
    await agregarDocumentosIflow(iflow.orgId, iflow.id, docsNuevos);
    setDocsNuevos([]);
    await onCambio();
  });

  const borrarConfirmado = () => accion(async () => {
    const c = confirmar;
    setConfirmar(null);
    if (!c) return;
    if (c.tipo === 'iflow') {
      await borrarIflow(iflow.orgId, iflow.id);
      onBorrado();
    } else {
      await borrarDocumentoIflow(iflow.orgId, iflow.id, c.id);
      await onCambio();
    }
  });

  const descargar = (fn: () => Promise<void>) => accion(fn);

  const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all disabled:opacity-40';
  const btnSec = `${btn} border-white/10 text-gray-200 hover:border-cyan-500/40 hover:text-white`;

  return (
    <div className="fixed inset-0 bg-black/70 flex justify-center items-center z-50 p-4">
      <div className={`bg-gray-800 border border-white/10 rounded-2xl w-full ${modo === 'ver' && pestana === 'diagrama' ? 'max-w-6xl' : 'max-w-4xl'} max-h-[calc(100vh-2rem)] flex flex-col`}>
        <header className="p-5 border-b border-white/10 space-y-3 flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-white break-words">{iflow.titulo}</h2>
              <p className="text-xs text-gray-500 font-mono mt-0.5">{iflow.idTecnico} · v{iflow.versionIflow || '—'} · revisión {iflow.versionActual} · {tamanoLegible(iflow.tamano)}</p>
            </div>
            <button onClick={onCerrar} disabled={ocupado} className="text-gray-400 hover:text-white disabled:opacity-40" aria-label="Cerrar">
              <X className="w-5 h-5" />
            </button>
          </div>
          {modo === 'ver' && (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => descargar(() => descargarIflow(iflow.orgId, iflow.id))} disabled={ocupado} className={`${btn} border-cyan-500/40 bg-cyan-600 text-white hover:bg-cyan-500`}>
                <Download className="w-3.5 h-3.5" /> Descargar .zip
              </button>
              <button onClick={() => descargar(() => descargarSinValores(iflow.orgId, iflow.id))} disabled={ocupado} className={btnSec}
                title="Copia del .zip sin direcciones, alias, parámetros ni URLs fijas, para compartir sin datos del cliente">
                <EyeOff className="w-3.5 h-3.5" /> Descargar sin valores
              </button>
              {onCrearDiseno && !soloLectura && (
                <button onClick={() => accion(async () => onCrearDiseno((await disenoDesdeIflow(iflow.orgId, iflow.id)).id))} disabled={ocupado} className={btnSec}
                  title="Crea un boceto editable a partir del diagrama de la revisión actual (sin valores)">
                  <PencilRuler className="w-3.5 h-3.5" /> Crear boceto
                </button>
              )}
              {esDueno && <button onClick={() => { setFocoAdaptar(''); setModo('adaptar'); }} className={btnSec}><SlidersHorizontal className="w-3.5 h-3.5" /> Adaptar valores</button>}
              {gestionar && <button onClick={() => setModo('version')} className={btnSec}><Upload className="w-3.5 h-3.5" /> Nueva versión</button>}
              {gestionar && <button onClick={() => { setDatos(datosEditables(iflow)); setModo('editar'); }} className={btnSec}><Pencil className="w-3.5 h-3.5" /> Editar datos</button>}
              {esPersonal && !soloLectura && <button onClick={() => setModo('publicar')} className={btnSec}><Send className="w-3.5 h-3.5" /> Publicar en un equipo</button>}
              {gestionar && (
                <button onClick={() => setConfirmar({ tipo: 'iflow' })} className={`${btn} border-red-500/30 text-red-300 hover:bg-red-500/10 ml-auto`}>
                  <Trash2 className="w-3.5 h-3.5" /> Borrar
                </button>
              )}
            </div>
          )}
        </header>

        <div ref={cuerpoRef} className="p-5 space-y-4 overflow-y-auto min-h-0 flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          {/* ── Ver ─────────────────────────────────────────────────── */}
          {modo === 'ver' && (
            <>
              <nav className="flex gap-1 border-b border-white/10 text-sm">
                {([
                  ['resumen', 'Resumen'],
                  ['diagrama', 'Diagrama'],
                  ['documentos', `Documentación (${iflow.documentos?.length || 0})`],
                  ['versiones', `Versiones (${iflow.versionActual})`]
                ] as [Pestana, string][]).map(([p, label]) => (
                  <button key={p} onClick={() => setPestana(p)} className={`px-3 py-2 -mb-px border-b-2 ${pestana === p ? 'border-cyan-400 text-cyan-300' : 'border-transparent text-gray-400 hover:text-white'}`}>
                    {label}
                  </button>
                ))}
              </nav>

              {pestana === 'resumen' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-300 whitespace-pre-wrap">{iflow.descripcion}</p>
                  <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Campo label="Estado">
                      {estado}
                      {iflow.aprobacion && (
                        <span className="block text-xs text-emerald-300 mt-0.5" title={fechaHora(iflow.aprobacion.fecha)}>
                          <BadgeCheck className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
                          por {iflow.aprobacion.nombre || 'alguien'} · rev. {iflow.aprobacion.revision}
                        </span>
                      )}
                      {iflow.aprobacion && iflow.aprobacion.revision < iflow.versionActual && (
                        <span className="block text-[11px] text-amber-300">la actual es la rev. {iflow.versionActual}</span>
                      )}
                    </Campo>
                    <Campo label="Sistemas">
                      <span className="inline-flex items-center gap-1">{iflow.sistemaOrigen || '—'}<ArrowRight className="w-3 h-3 text-cyan-400" />{iflow.sistemaDestino || '—'}</span>
                    </Campo>
                    <Campo label="Solución">{iflow.solucion || '—'}</Campo>
                    <Campo label="Proyecto">{proyecto ? proyecto.nombre : iflow.proyectoId ? '(sin acceso)' : '—'}</Campo>
                    <Campo label="Autor">{iflow.autorNombre || '—'}</Campo>
                    <Campo label="Creado">{fechaHora(iflow.creadoEn) || '—'}</Campo>
                    <Campo label="Actualizado">{fechaHora(iflow.actualizadoEn) || '—'}</Campo>
                    <Campo label="Etiquetas">{iflow.etiquetas.length ? iflow.etiquetas.map((t) => `#${t}`).join(' ') : '—'}</Campo>
                  </dl>
                  {(iflow.publicaciones || []).length > 0 && (
                    <p className="text-xs text-gray-400 flex items-center gap-1.5 flex-wrap">
                      <Send className="w-3.5 h-3.5" /> Publicado en:{' '}
                      {(iflow.publicaciones || []).map((p) => `${nombreOrg(p.orgId)} (rev. ${p.revisionPublicada})`).join(' · ')}
                    </p>
                  )}
                  {iflow.publicadoDesdePersonal && (
                    <p className="text-xs text-gray-400 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Publicado desde el repositorio personal de su autor.</p>
                  )}
                  {iflow.notas && (
                    <div>
                      <h4 className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Notas de configuración</h4>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap bg-white/5 border border-white/10 rounded-lg p-3">{iflow.notas}</p>
                    </div>
                  )}
                  {iflow.tieneValoresFijos && (
                    <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-300 p-3 rounded-lg text-xs">
                      <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      El .zip tiene direcciones o alias fijos (no se muestran acá). Revísalos al importarlo en otro tenant.
                    </div>
                  )}
                  <DetalleTecnico {...iflow} />
                </div>
              )}

              {pestana === 'diagrama' && (
                <DiagramaIflow
                  orgId={iflow.orgId}
                  iflowId={iflow.id}
                  versionActual={iflow.versionActual}
                  nombreArchivo={iflow.idTecnico || 'iflow'}
                  // Adjuntar el PNG: mismas reglas que "Documentación" (gestionar y con cupo).
                  onAdjuntar={gestionar && (iflow.documentos?.length || 0) < MAX_DOCUMENTOS
                    ? async (archivo) => { await agregarDocumentosIflow(iflow.orgId, iflow.id, [archivo]); await onCambio(); }
                    : undefined}
                  onEditar={esDueno ? (foco) => { setFocoAdaptar(foco); setModo('adaptar'); } : undefined}
                  puedeGuardar={!soloLectura}
                  onGuardado={async (version, cambiados) => {
                    await onCambio();
                    onAviso(`Versión ${version} guardada con ${cambiados} valor${cambiados === 1 ? '' : 'es'} adaptado${cambiados === 1 ? '' : 's'}.`);
                  }}
                />
              )}

              {pestana === 'documentos' && (
                <div className="space-y-3">
                  {(iflow.documentos || []).length === 0 && <p className="text-sm text-gray-500">Este iFlow no tiene documentación adjunta.</p>}
                  {(iflow.documentos || []).map((d) => (
                    <div key={d.id} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
                      <FileText className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                      <span className="text-white truncate flex-1">{d.nombre}</span>
                      <span className="text-xs text-gray-500 hidden sm:inline">{tamanoLegible(d.tamano)}{fechaHora(d.fecha) && ` · ${fechaHora(d.fecha)}`}</span>
                      <button onClick={() => descargar(() => descargarDocumentoIflow(iflow.orgId, iflow.id, d.id))} disabled={ocupado} className="text-cyan-300 hover:text-cyan-200 disabled:opacity-40" aria-label={`Descargar ${d.nombre}`}>
                        <Download className="w-4 h-4" />
                      </button>
                      {gestionar && (
                        <button onClick={() => setConfirmar({ tipo: 'documento', id: d.id, nombre: d.nombre })} disabled={ocupado} className="text-gray-400 hover:text-red-400 disabled:opacity-40" aria-label={`Quitar ${d.nombre}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {gestionar && (iflow.documentos?.length || 0) < MAX_DOCUMENTOS && (
                    <div className="pt-2 space-y-2">
                      <SelectorDocumentos archivos={docsNuevos} onChange={setDocsNuevos} onError={setError} existentes={iflow.documentos?.length || 0} />
                      {docsNuevos.length > 0 && (
                        <button onClick={subirDocumentos} disabled={ocupado} className={`${btn} border-cyan-500/40 bg-cyan-600 text-white hover:bg-cyan-500`}>
                          {ocupado ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Subir {docsNuevos.length} documento{docsNuevos.length === 1 ? '' : 's'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {pestana === 'versiones' && (
                versiones === null ? (
                  <div className="p-6 text-center text-gray-400"><RefreshCw className="w-5 h-5 animate-spin mx-auto" /></div>
                ) : (
                  <div className="space-y-4">
                  <ul className="space-y-2">
                    {versiones.map((v) => (
                      <li key={v.numero} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                        <History className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white">
                            Revisión {v.numero} <span className="text-gray-500 font-mono text-xs">· v{v.versionIflow || '—'} · {tamanoLegible(v.tamano)}</span>
                            {v.numero === iflow.versionActual && <span className="ml-2 text-[11px] text-emerald-300">actual</span>}
                          </p>
                          <p className="text-xs text-gray-500">{fechaHora(v.fecha)}{v.subidoPor === user?.uid ? ' · tú' : ''}</p>
                          {v.nota && <p className="text-xs text-gray-300 mt-1">{v.nota}</p>}
                        </div>
                        <button onClick={() => descargar(() => descargarIflow(iflow.orgId, iflow.id, v.numero))} disabled={ocupado} className="text-cyan-300 hover:text-cyan-200 disabled:opacity-40" aria-label={`Descargar revisión ${v.numero}`}>
                          <Download className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                  {(iflow.historialEstado || []).length > 0 && (
                    <div>
                      <h4 className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Cambios de estado</h4>
                      <ul className="space-y-1">
                        {[...(iflow.historialEstado || [])].reverse().map((h, i) => (
                          <li key={i} className="text-xs text-gray-300 flex items-center gap-2">
                            <BadgeCheck className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0" />
                            <span>
                              {ESTADOS_IFLOW.find((e) => e.value === h.estado)?.label || h.estado} · rev. {h.revision} · {h.motivo === 'nueva-revision' ? `automático (nueva revisión de ${h.nombre || 'alguien'})` : h.nombre || 'alguien'}
                              <span className="text-gray-500"> · {fechaHora(h.fecha)}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  </div>
                )
              )}
            </>
          )}

          {/* ── Adaptar valores (E3) ────────────────────────────────── */}
          {modo === 'adaptar' && (
            <AdaptarValores
              iflow={iflow}
              foco={focoAdaptar}
              puedeGuardar={!soloLectura}
              onGuardado={async (version, cambiados) => {
                await onCambio();
                onAviso(`Versión ${version} guardada con ${cambiados} valor${cambiados === 1 ? '' : 'es'} adaptado${cambiados === 1 ? '' : 's'}.`);
                volver();
              }}
            />
          )}

          {/* ── Editar ──────────────────────────────────────────────── */}
          {modo === 'editar' && <FormDatosIflow orgId={iflow.orgId} datos={datos} onChange={setDatos} />}

          {/* ── Nueva versión ───────────────────────────────────────── */}
          {modo === 'version' && (
            !analisis ? (
              <>
                <p className="text-sm text-gray-300">Sube el .zip actualizado de <span className="font-mono text-white">{iflow.idTecnico}</span> (debe ser el mismo iFlow, con cambios).</p>
                {iflow.estado !== 'borrador' && (
                  <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 p-3 rounded-lg text-xs">
                    Está marcado como "{estado}" en la revisión {iflow.aprobacion?.revision ?? iflow.versionActual}. Al subir un .zip nuevo vuelve a <strong>Borrador</strong> hasta que alguien lo marque de nuevo.
                  </div>
                )}
                <ElegirZip ocupado={ocupado} nombre={zipNuevo?.name} onElegir={elegirVersion} />
              </>
            ) : (
              <>
                <DetalleTecnico {...analisis} />
                <AvisosAnalisis
                  analisis={analisis}
                  confirmaFijos={confirmaFijos}
                  onConfirmaFijos={setConfirmaFijos}
                  vaciarParametros={vaciarParametros}
                  onVaciarParametros={setVaciarParametros}
                />
                <div>
                  <label className={labelCls}>Nota de la versión (opcional)</label>
                  <input className={inputCls} maxLength={500} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Qué cambió…" />
                </div>
              </>
            )
          )}

          {/* ── Publicar ────────────────────────────────────────────── */}
          {modo === 'publicar' && (
            destinos.length === 0 ? (
              <p className="text-sm text-gray-400">No perteneces a ninguna organización de equipo activa donde puedas publicar.</p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-300">Se copia la revisión actual, con su documentación, a la organización elegida. Tu copia personal se mantiene; el proyecto asociado no viaja.</p>
                {publicacionPrevia && (
                  <div className="bg-blue-500/10 border border-blue-500/20 text-blue-200 p-3 rounded-lg text-xs">
                    Ya lo publicaste ahí (tu revisión {publicacionPrevia.revisionPublicada}{fechaHora(publicacionPrevia.fecha) && `, ${fechaHora(publicacionPrevia.fecha)}`}).
                    Se agregará la revisión {iflow.versionActual} como <strong>nueva versión de esa copia</strong>, con los documentos que todavía no tenga — no se crea un duplicado.
                    Si esa copia fue borrada, se publica una nueva.
                  </div>
                )}
                <div>
                  <label className={labelCls}>Organización destino</label>
                  <select className={inputCls} value={destino} onChange={(e) => setDestino(e.target.value)}>
                    <option value="">Elige una organización…</option>
                    {destinos.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </div>
              </div>
            )
          )}
        </div>

        {modo !== 'ver' && (
          <footer className="flex justify-between gap-3 p-5 border-t border-white/10 flex-shrink-0">
            <button onClick={volver} disabled={ocupado} className="px-4 py-2 text-sm text-gray-300 hover:text-white disabled:opacity-40">Cancelar</button>
            {modo === 'editar' && (
              <button onClick={guardarEdicion} disabled={ocupado} className="px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-40 inline-flex items-center gap-2">
                {ocupado && <RefreshCw className="w-4 h-4 animate-spin" />} Guardar cambios
              </button>
            )}
            {modo === 'version' && analisis && (
              <button onClick={guardarVersion} disabled={ocupado || (analisis.valoresFijos.length > 0 && !confirmaFijos)} className="px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-40 inline-flex items-center gap-2">
                {ocupado && <RefreshCw className="w-4 h-4 animate-spin" />} Subir versión {iflow.versionActual + 1}
              </button>
            )}
            {modo === 'publicar' && destinos.length > 0 && (
              <button onClick={publicar} disabled={ocupado || !destino} className="px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-40 inline-flex items-center gap-2">
                {ocupado && <RefreshCw className="w-4 h-4 animate-spin" />} {publicacionPrevia ? 'Actualizar publicación' : 'Publicar'}
              </button>
            )}
          </footer>
        )}
      </div>

      {confirmar && (
        <ConfirmDialog
          title={confirmar.tipo === 'iflow' ? 'Borrar iFlow' : 'Quitar documento'}
          message={confirmar.tipo === 'iflow'
            ? `Se borrará "${iflow.titulo}" con sus ${iflow.versionActual} versión(es) y su documentación. No se puede deshacer.`
            : `Se quitará "${confirmar.nombre}". No se puede deshacer.`}
          confirmLabel={confirmar.tipo === 'iflow' ? 'Borrar' : 'Quitar'}
          danger
          onConfirm={borrarConfirmado}
          onCancel={() => setConfirmar(null)}
        />
      )}
    </div>
  );
}
